import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { State } from '../../store/auth/auth.reducer';
import * as pwValidator from './pw-validators';
import * as fromAuth from '../../store/auth/';
import { TranslateService } from '@ngx-translate/core';
import { UserAuthKeyExpiration } from '../../store/auth/auth-key-expired.model';
import * as fromRouting from '../../../core/store/routing';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
    selector: 'topo-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  public expirationStatus$: Observable<UserAuthKeyExpiration>;
  public destroy$ = new Subject<void>();
  public errorMessage$: Observable<string>;
  code: string;
  username: string;
  showConfirm: boolean;
  showConfirmReset: boolean;
  isInvite: boolean;
  isExp: UserAuthKeyExpiration;

  constructor(
    private activatedRoute: ActivatedRoute,
    private notify: SystemNotificationsService,
    private translateService: TranslateService,
    private store: Store<State>,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        newPassword: [
          null,
          [Validators.required, Validators.minLength(8), Validators.nullValidator, pwValidator.PasswordStrength, pwValidator.SpecialChar]
        ],
        passwordConfirm: [null, Validators.required]
      },
      { validators: [pwValidator.matchPasswordValidator] }
    );

    this.errorMessage$ = this.store.select(fromAuth.selectError);

    // Determines if it should show messages from reset password or setup pass for a new user
    this.activatedRoute.url.pipe(takeUntil(this.destroy$)).subscribe(params => (this.isInvite = params[0].path == 'setup-password'));

    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.code = decodeURI(params['code']);
      this.username = decodeURI(params['user']);
    });

    if (this.isInvite) {
      this.store.dispatch(fromAuth.isInviteCodeExpired({ username: this.username, authKey: this.code }));
    } else {
      this.store.dispatch(fromAuth.isResetCodeExpired({ username: this.username, authKey: this.code }));
    }

    this.expirationStatus$ = this.store.select(fromAuth.selectIsExpired);

    this.errorMessage$
      .pipe(
        filter(err => !!err),
        tap(value => {
          value = `ERRORS.${value}`;
          this.translateService.get(value).subscribe(res => {
            this.notify.error(res);
          });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updatePassword(): void {
    const password = this.form.value.newPassword;

    if (this.isInvite) {
      this.store.dispatch(fromAuth.setNewUserPassword({ username: this.username, newPassword: password, authKey: this.code }));
    } else {
      this.store.dispatch(fromAuth.setUserPassword({ username: this.username, newPassword: password, authKey: this.code }));
    }
  }

  verifyResend(): void {
    this.showConfirm = true;
  }

  resetPasswordConfirmation(): void {
    if(this.form.valid) {
      this.showConfirmReset = true;
    }
  }

  confirmResendLinkResponse(response: boolean): void {
    this.showConfirm = false;

    if (response) {
      this.resendCode();
    }
  }

  confirmResetResponse(response: boolean): void {
    this.showConfirmReset = false;

    if (response) {
      this.updatePassword();
    }
  }

  resendCode(): void {
    if (this.isInvite) {
      this.store.dispatch(fromAuth.resendInviteLink({ username: this.username }));
    } else {
      this.store.dispatch(fromAuth.sendPasswordResetLink({ username: this.username }));
    }
  }

  backToLogin(): void {
    this.store.dispatch(
      fromRouting.go({
        routeData: { path: ['login'] }
      })
    );
  }
}
